/**
 * One mutable value and its subscribers, read through `useSyncExternalStore`, so a change
 * re-renders only the components that subscribe. On a React Context, every consumer re-renders on
 * any change. It holds high-frequency state with few readers, such as `hoveredNodePath`, which
 * only `<HoverHighlight>` reads.
 */
import { useSyncExternalStore } from 'react';
/**
 * @canonical A value that changes often and that few components read
 */
export function createExternalStore(initial) {
    let value = initial;
    const listeners = new Set();
    return {
        get: () => value,
        set: (next) => {
            if (Object.is(next, value))
                return;
            value = next;
            notifyAll(listeners);
        },
        subscribe: (onStoreChange) => {
            listeners.add(onStoreChange);
            return () => {
                listeners.delete(onStoreChange);
            };
        },
    };
}
function notifyAll(listeners) {
    const failures = [];
    for (const listener of listeners) {
        try {
            listener();
        }
        catch (error) {
            failures.push(error);
        }
    }
    if (failures.length > 0)
        throw failures[0];
}
/**
 * Subscribes the calling component to an {@link ExternalStore}'s value.
 *
 * @canonical A value that changes often and that few components read
 */
export function useExternalStoreValue(store) {
    return useSyncExternalStore(store.subscribe, store.get, store.get);
}
/**
 * Subscribes the calling component to the part of an {@link ExternalStore}'s value that `select`
 * picks, so a change elsewhere in the value renders nothing. `select` returns a value already in
 * the store, or a primitive, so an unchanged pick is `Object.is`-equal.
 *
 * @canonical A value that changes often and that few components read
 */
export function useExternalStoreSelection(store, select) {
    const pick = () => select(store.get());
    return useSyncExternalStore(store.subscribe, pick, pick);
}
//# sourceMappingURL=createExternalStore.js.map